import type { LandingContent } from '@/interfaces/LandingContent';

import type { SupportedLocale } from './logic';

export type { LandingContent, LandingDownloadUnavailableNoticeContent, LandingHeroRotatorContent } from '@/interfaces/LandingContent';

const RU: LandingContent = {
    kicker: 'Платформа безопасной коммуникации',
    promise: 'Chattenger — безграничные возможности',
    title: 'Экосистемы коммуникаций для команд, компаний и сообществ',
    description: 'Суверенная коммуникационная платформа для союзов, государств, компаний и команд.',
    downloadUnavailableNotice: {
        description: 'Сборка ещё не загружена или временно недоступна. Попробуйте снова чуть позже.',
        title: 'Предпросмотр Android APK временно недоступен',
    },
    appDownloadCtaLabel: 'Скачать приложение',
    signInPrompt: 'Уже есть аккаунт?',
    signInCtaLabel: 'Войти',
    aliases: 'Chattenger. Чаттенджер. Чатенджер.',
    heroPills: ['Безопасность по умолчанию', 'Web + mobile'],
    signalItems: [
        'Личные и командные коммуникации: чаты, каналы, группы и звонки.',
        'Единое пространство общения на телефоне и в браузере.',
        'Контроль, поддержка и записи в одном продукте.',
    ],
    featurePairs: [
        ['Чаты', 'Каналы и группы'],
        ['Звонки', 'Поиск по истории'],
        ['Голосовые сообщения', 'Кружочки'],
        ['Отложенная отправка', 'Предпросмотр медиа'],
        ['Администрирование', 'Сквозное шифрование (AES-256)'],
        ['Файлы, фото и видео', 'Конференции и шаринг экрана'],
    ],
    extensionsTitle: 'Дополняем под ваши задачи',
    extensionItems: [
        'Мини-аппы',
        'Платежи',
        'Док-оборот',
        'CRM',
        'KYC',
        'Логистика',
        'Маркетплейсы',
        'Медиа',
        'Образование',
        'Госсектор',
        'Новости',
        'Сообщества',
    ],
    architectureTitle: 'Безопасность и архитектура',
    architectureCopy:
        'Chattenger создаётся как независимая коммуникационная платформа с сильной криптографией, устойчивой связью и контролем над критической инфраструктурой.',
    architectureItems: [
        'AES-256-CBC end-to-end: шифрование и дешифрование выполняются локально на устройстве пользователя.',
        'Собственная звонковая инфраструктура рассчитана на конференции, совместную работу и рост нагрузки.',
        'Независимый серверный контур спроектирован для масштабирования и устойчивой работы.',
        'Асимметричное шифрование ключей, строгая валидация сообщений и гарантия корректной доставки.',
    ],
    outOfBoxTitle: 'Из коробки',
    outOfBoxItems: [
        'Личные и групповые чаты, файлы и поиск по истории.',
        'Каналы и супергруппы 100k+, роли и модерация.',
        'Аудиосообщения, отложенная и запланированная отправка.',
        'Групповые и конференц-звонки 1000+ участников, видео и демонстрация экрана.',
        'Предпросмотр документов, медиа и вложений без лишних переходов.',
        'Медиа: circle video, редактор видео и предпросмотр документов.',
    ],
    investorsEyebrow: 'BRICS / SCO / B2B / B2C',
    investorsTitle:
        'Chattenger — это коммуникационное ядро, которое можно развивать в сторону суверенных цифровых экосистем, госуслуг, финансов, логистики и отраслевых super-app поверхностей.',
    investorsDescription:
        'Защищённая коммуникация, цифровой суверенитет, расширяемая mini-app архитектура и путь от доверенного мессенджера к более крупной экономической инфраструктуре.',
    investorsNowTitle: 'Что уже есть',
    investorsNowList: [
        'Чаты, звонки, групповые комнаты, обмен медиа, уведомления и записи.',
        'Единый пользовательский опыт в вебе и на телефоне.',
        'Администрирование, поддержка и защищённые рабочие поверхности.',
    ],
    investorsDirectionTitle: 'Куда платформа идёт',
    investorsDirectionList: [
        'Mini-apps, KYC, платежи, логистика, CRM, медиа и госсектор.',
        'Коммуникационная экосистема масштаба альянсов и государств, а не только consumer messenger.',
        'Гибкая архитектура для региональных развёртываний и отраслевых контуров.',
    ],
    contactBackHomeLabel: 'На главную',
    contactCompanyLabel: 'Клиент',
    contactCopyEmailFailedLabel: 'Не удалось скопировать email',
    contactCopyEmailLabel: 'Скопировать email',
    contactCopyEmailSuccessLabel: 'Email скопирован',
    contactDetailsLinkLabel: 'Контакты и реквизиты',
    contactEmailLabel: 'Email',
    contactFooterCopy: 'Сервис Chattenger предоставляется индивидуальным предпринимателем.',
    contactFooterTitle: 'Информация о клиенте',
    contactInnLabel: 'ИНН',
    contactLegalNameLabel: 'Клиент',
    contactOgrnipLabel: 'ОГРНИП',
    contactPageDescription: 'Информация о клиенте, предоставляющем сервис Chattenger.',
    contactPageTitle: 'Контакты',
    contactPhoneLabel: 'Телефон',
    contactWebsiteLabel: 'Сайт',
};

const EN: LandingContent = {
    kicker: 'Secure communications platform',
    promise: 'Chattenger — limitless opportunities',
    title: 'Communication ecosystems for teams, companies, and communities',
    description: 'A sovereign communications platform for alliances, governments, companies, and teams.',
    downloadUnavailableNotice: {
        description: 'The build is still uploading or temporarily unavailable. Please try again shortly.',
        title: 'Preview Android APK is temporarily unavailable',
    },
    appDownloadCtaLabel: 'Get the app',
    signInPrompt: 'Already have an account?',
    signInCtaLabel: 'Sign in',
    aliases: 'Chattenger. Чаттенджер. Chatenjer.',
    heroPills: ['Secure by design', 'Web + mobile'],
    signalItems: [
        'Private and team communications: chats, channels, groups, and calls.',
        'One conversation space across phone and browser.',
        'Control, support, and recordings in one product.',
    ],
    featurePairs: [
        ['Chats', 'Channels and groups'],
        ['Calls', 'History search'],
        ['Voice messages', 'Circle videos'],
        ['Scheduled send', 'Media previews'],
        ['Administration', 'End-to-end encryption (AES-256)'],
        ['Files, photos, and video', 'Conferences and screen sharing'],
    ],
    extensionsTitle: 'Extended for your needs',
    extensionItems: [
        'Mini-apps',
        'Payments',
        'Document flow',
        'CRM',
        'KYC',
        'Logistics',
        'Marketplaces',
        'Media',
        'Education',
        'Public sector',
        'News',
        'Communities',
    ],
    architectureTitle: 'Security & architecture',
    architectureCopy:
        'Chattenger is being built as an independent communications platform with strong cryptography, resilient calling, and control over critical infrastructure.',
    architectureItems: [
        'AES-256-CBC end-to-end: encryption and decryption happen locally on the user device.',
        'A dedicated call backbone is designed for conferencing, collaboration, and sustained load.',
        'An independent server estate is shaped for scale, resilience, and operational control.',
        'Asymmetric key handling, strict message validation, and dependable delivery guarantees.',
    ],
    outOfBoxTitle: 'Out-of-the-box',
    outOfBoxItems: [
        'Private and group chats, file sharing, and history search.',
        'Channels and supergroups 100k+, with roles and moderation.',
        'Voice messages, scheduled send, and delayed delivery.',
        'Group calls, video, screen share, and recordings.',
        'Document, media, and attachment previews without unnecessary context switches.',
        'Media features such as circle video, video editing, and document preview.',
    ],
    investorsEyebrow: 'BRICS / SCO / B2B / B2C',
    investorsTitle:
        'Chattenger is a communications core that can extend into sovereign digital ecosystems, public services, finance, logistics, and industry-specific super-app surfaces.',
    investorsDescription:
        'Secure communications, digital sovereignty, extensible mini-app architecture, and the path from trusted messaging to larger economic infrastructure.',
    investorsNowTitle: 'Core now',
    investorsNowList: [
        'Chats, calls, group rooms, media exchange, notifications, and recordings.',
        'One user experience across web and mobile.',
        'Admin, support, and protected work surfaces.',
    ],
    investorsDirectionTitle: 'Platform direction',
    investorsDirectionList: [
        'Mini-apps, KYC, payments, logistics, CRM, media, and public-sector surfaces.',
        'Alliance-scale and state-scale communications, not only a consumer messenger.',
        'Flexible architecture for regional rollouts and sector-specific environments.',
    ],
    contactBackHomeLabel: 'Home',
    contactCompanyLabel: 'Customer',
    contactCopyEmailFailedLabel: 'Could not copy email',
    contactCopyEmailLabel: 'Copy email',
    contactCopyEmailSuccessLabel: 'Email copied',
    contactDetailsLinkLabel: 'Contacts and details',
    contactEmailLabel: 'Email',
    contactFooterCopy: 'The Chattenger service is provided by an individual entrepreneur.',
    contactFooterTitle: 'Customer information',
    contactInnLabel: 'INN',
    contactLegalNameLabel: 'Customer',
    contactOgrnipLabel: 'OGRNIP',
    contactPageDescription: 'Customer information for the operator providing the Chattenger service.',
    contactPageTitle: 'Contacts',
    contactPhoneLabel: 'Phone',
    contactWebsiteLabel: 'Website',
};

const AR: LandingContent = {
    kicker: 'منصة اتصالات آمنة',
    promise: 'Chattenger — فرص بلا حدود',
    title: 'منظومات اتصالات للفرق والشركات والمجتمعات',
    description: 'منصة اتصالات سيادية للتحالفات والحكومات والشركات والفرق.',
    downloadUnavailableNotice: {
        description: 'ما زال الإصدار قيد الرفع أو غير متاح مؤقتاً. حاول مرة أخرى بعد قليل.',
        title: 'ملف APK التجريبي لأندرويد غير متاح مؤقتاً',
    },
    appDownloadCtaLabel: 'احصل على التطبيق',
    signInPrompt: 'لديك حساب بالفعل؟',
    signInCtaLabel: 'تسجيل الدخول',
    aliases: 'Chattenger. Чаттенджер. شاتنجر.',
    heroPills: ['الأمان أولاً', 'الويب + الجوال'],
    signalItems: [
        'اتصالات خاصة واتصالات فرق: محادثات وقنوات ومجموعات ومكالمات.',
        'مساحة محادثة واحدة بين الهاتف والمتصفح.',
        'التحكم والدعم والتسجيلات داخل منتج واحد.',
    ],
    featurePairs: [
        ['المحادثات', 'القنوات والمجموعات'],
        ['المكالمات', 'البحث في السجل'],
        ['الرسائل الصوتية', 'الفيديو الدائري'],
        ['الإرسال المجدول', 'معاينة الوسائط'],
        ['الإدارة والدعم', 'تشفير طرفي AES-256'],
        ['الملفات والصور والفيديو', 'المؤتمرات ومشاركة الشاشة'],
    ],
    extensionsTitle: 'توسعات حسب احتياجكم',
    extensionItems: [
        'Mini-apps',
        'المدفوعات',
        'إدارة المستندات',
        'CRM',
        'KYC',
        'اللوجستيات',
        'الأسواق',
        'الإعلام',
        'التعليم',
        'القطاع العام',
        'الأخبار',
        'المجتمعات',
    ],
    architectureTitle: 'الأمان والهندسة',
    architectureCopy: 'يُبنى Chattenger كمنصة اتصالات مستقلة تجمع بين التشفير القوي والاتصال المستقر والتحكم في البنية التحتية الحرجة.',
    architectureItems: [
        'AES-256-CBC: يتم التشفير وفك التشفير محلياً على جهاز المستخدم.',
        'بنية المكالمات مهيأة للمؤتمرات والتعاون والاستقرار تحت الضغط.',
        'البنية الخادمية صُممت للتوسع والاعتمادية والتحكم التشغيلي.',
        'تحقق صارم من الرسائل وتسليم موثوق وإدارة مفاتيح غير متماثلة.',
    ],
    outOfBoxTitle: 'جاهز من البداية',
    outOfBoxItems: [
        'محادثات خاصة وجماعية وملفات وبحث في السجل.',
        'قنوات ومجموعات كبيرة مع أدوار وإشراف.',
        'رسائل صوتية وفيديو دائري وإرسال مجدول.',
        'مكالمات جماعية وفيديو ومشاركة شاشة وتسجيلات.',
        'معاينة المستندات والوسائط والمرفقات دون انتقالات غير ضرورية.',
        'قابلية توسع لـ mini-apps والمنتجات القطاعية.',
    ],
    investorsEyebrow: 'BRICS / SCO / B2B / B2C',
    investorsTitle: 'Chattenger هو نواة اتصالات يمكن توسيعها إلى منظومات رقمية سيادية وخدمات عامة ومالية ولوجستية وأسقف super-app متخصصة.',
    investorsDescription: 'اتصالات آمنة وسيادة رقمية وبنية mini-app قابلة للتمدد ومسار من المراسلة الموثوقة إلى بنية اقتصادية أكبر.',
    investorsNowTitle: 'الأساس الآن',
    investorsNowList: [
        'محادثات ومكالمات وغرف جماعية وتبادل وسائط وإشعارات وتسجيلات.',
        'تجربة استخدام واحدة عبر الويب والجوال.',
        'الإدارة والدعم والأسطح المحمية للعمل.',
    ],
    investorsDirectionTitle: 'اتجاه المنصة',
    investorsDirectionList: [
        'Mini-apps وKYC والمدفوعات واللوجستيات وCRM والإعلام والقطاع العام.',
        'اتصالات على مستوى التحالفات والدول، وليس مجرد messenger استهلاكي.',
        'هندسة مرنة للإطلاقات الإقليمية والبيئات القطاعية.',
    ],
    contactBackHomeLabel: 'الرئيسية',
    contactCompanyLabel: 'العميل',
    contactCopyEmailFailedLabel: 'تعذر نسخ البريد الإلكتروني',
    contactCopyEmailLabel: 'نسخ البريد الإلكتروني',
    contactCopyEmailSuccessLabel: 'تم نسخ البريد الإلكتروني',
    contactDetailsLinkLabel: 'جهات الاتصال والبيانات',
    contactEmailLabel: 'البريد',
    contactFooterCopy: 'يتم تقديم خدمة Chattenger بواسطة صاحب عمل فردي.',
    contactFooterTitle: 'معلومات العميل',
    contactInnLabel: 'INN',
    contactLegalNameLabel: 'العميل',
    contactOgrnipLabel: 'OGRNIP',
    contactPageDescription: 'معلومات العميل الذي يقدم خدمة Chattenger.',
    contactPageTitle: 'جهات الاتصال',
    contactPhoneLabel: 'الهاتف',
    contactWebsiteLabel: 'الموقع',
};

const ZH_CN: LandingContent = {
    kicker: '安全通信平台',
    promise: 'Chattenger — 无限可能',
    title: '面向团队、企业与社群的通信生态',
    description: '面向联盟、政府、企业与团队的主权通信平台。',
    downloadUnavailableNotice: {
        description: '构建仍在上传中或暂时不可用，请稍后再试。',
        title: '预览版 Android APK 暂时不可用',
    },
    appDownloadCtaLabel: '获取应用',
    signInPrompt: '已经有账号？',
    signInCtaLabel: '登录',
    aliases: 'Chattenger. Чаттенджер. Chatenjier.',
    heroPills: ['默认安全', 'Web + mobile'],
    signalItems: ['面向个人与团队的通信：聊天、频道、群组与通话。', '手机与浏览器之间共享同一会话空间。', '管理、支持与录制整合在一个产品中。'],
    featurePairs: [
        ['聊天', '频道与群组'],
        ['通话', '历史搜索'],
        ['语音消息', '圆形视频'],
        ['定时发送', '媒体预览'],
        ['管理与支持', 'AES-256 端到端加密'],
        ['文件、照片与视频', '会议与屏幕共享'],
    ],
    extensionsTitle: '按你的业务继续扩展',
    extensionItems: ['Mini-apps', '支付', '文档流转', 'CRM', 'KYC', '物流', '市场平台', '媒体', '教育', '公共部门', '新闻', '社群'],
    architectureTitle: '安全与架构',
    architectureCopy: 'Chattenger 正在被构建为一套独立通信平台，兼顾强加密、稳定通话与对关键基础设施的掌控。',
    architectureItems: [
        'AES-256-CBC：加密与解密在用户设备本地完成。',
        '专用通话底座面向会议、协作与持续负载而设计。',
        '独立服务基础设施面向扩展、稳定性与运维掌控。',
        '严格消息校验、非对称密钥处理与可靠交付保障。',
    ],
    outOfBoxTitle: '开箱即用',
    outOfBoxItems: [
        '私聊、群聊、文件与历史搜索。',
        '频道与超级群组，支持角色与审核。',
        '语音消息、圆形视频与定时发送。',
        '群组通话、视频、屏幕共享与录制。',
        '文档、媒体和附件预览，减少不必要的切换。',
        '可扩展到 mini-app 与行业产品。',
    ],
    investorsEyebrow: 'BRICS / SCO / B2B / B2C',
    investorsTitle: 'Chattenger 是一个通信核心，可延展到主权数字生态、公共服务、金融、物流与行业 super-app 场景。',
    investorsDescription: '安全通信、数字主权、可扩展的 mini-app 架构，以及从可信消息到更大经济基础设施的路径。',
    investorsNowTitle: '当前核心',
    investorsNowList: ['聊天、通话、群组房间、媒体交换、通知与录制。', '统一的 web 与 mobile 使用体验。', '管理、支持与受保护的工作界面。'],
    investorsDirectionTitle: '平台方向',
    investorsDirectionList: [
        'Mini-app、KYC、支付、物流、CRM、媒体与公共部门。',
        '面向联盟与国家级规模的通信，而不仅仅是消费级 messenger。',
        '面向区域化部署与行业场景的灵活架构。',
    ],
    contactBackHomeLabel: '首页',
    contactCompanyLabel: '客户',
    contactCopyEmailFailedLabel: '无法复制邮箱',
    contactCopyEmailLabel: '复制邮箱',
    contactCopyEmailSuccessLabel: '邮箱已复制',
    contactDetailsLinkLabel: '联系方式和登记信息',
    contactEmailLabel: '邮箱',
    contactFooterCopy: 'Chattenger 服务由个体经营者提供。',
    contactFooterTitle: '客户信息',
    contactInnLabel: 'INN',
    contactLegalNameLabel: '客户',
    contactOgrnipLabel: 'OGRNIP',
    contactPageDescription: '提供 Chattenger 服务的客户信息。',
    contactPageTitle: '联系方式',
    contactPhoneLabel: '电话',
    contactWebsiteLabel: '网站',
};

const ZH_HK: LandingContent = {
    kicker: '安全通訊平台',
    promise: 'Chattenger — 無限可能',
    title: '面向團隊、企業與社群的通訊生態',
    description: '面向聯盟、政府、企業與團隊的主權通訊平台。',
    downloadUnavailableNotice: {
        description: '建置仍在上傳中或暫時不可用，請稍後再試。',
        title: '預覽版 Android APK 暫時不可用',
    },
    appDownloadCtaLabel: '取得應用程式',
    signInPrompt: '已經有帳戶？',
    signInCtaLabel: '登入',
    aliases: 'Chattenger. Чаттенджер. Chatenjier.',
    heroPills: ['預設安全', 'Web + mobile'],
    signalItems: ['面向個人同團隊嘅通訊：聊天、頻道、群組同通話。', '手機同瀏覽器之間共享同一個對話空間。', '管理、支援同錄製集中喺一個產品入面。'],
    featurePairs: [
        ['聊天', '頻道與群組'],
        ['通話', '歷史搜尋'],
        ['語音訊息', '圓形影片'],
        ['預約發送', '媒體預覽'],
        ['管理與支援', 'AES-256 端對端加密'],
        ['檔案、相片與影片', '會議與螢幕分享'],
    ],
    extensionsTitle: '按你的業務延展',
    extensionItems: ['Mini-apps', '支付', '文件流轉', 'CRM', 'KYC', '物流', 'Marketplace', '媒體', '教育', '公共部門', '新聞', '社群'],
    architectureTitle: '安全與架構',
    architectureCopy: 'Chattenger 正被打造為一套獨立通訊平台，同時兼顧強加密、穩定通話同對關鍵基建嘅掌控。',
    architectureItems: [
        'AES-256-CBC：加密與解密在用戶裝置本地完成。',
        '專用通話底座面向會議、協作同持續負載而設計。',
        '獨立服務基建面向擴展、穩定性同營運掌控。',
        '嚴格訊息校驗、非對稱金鑰處理同可靠交付保障。',
    ],
    outOfBoxTitle: '開箱即用',
    outOfBoxItems: [
        '私聊、群聊、檔案與歷史搜尋。',
        '頻道與超級群組，支援角色與審核。',
        '語音訊息、圓形影片與預約發送。',
        '群組通話、影片、螢幕分享與錄製。',
        '文件、媒體同附件預覽，減少不必要切換。',
        '可擴展到 mini-app 與行業產品。',
    ],
    investorsEyebrow: 'BRICS / SCO / B2B / B2C',
    investorsTitle: 'Chattenger 是一個通訊核心，可延展到主權數碼生態、公共服務、金融、物流與行業 super-app 場景。',
    investorsDescription: '安全通訊、數碼主權、可擴展的 mini-app 架構，以及由可信訊息走向更大經濟基礎設施的路徑。',
    investorsNowTitle: '現有能力',
    investorsNowList: ['聊天、通話、群組房間、媒體交換、通知與錄製。', 'Web 同手機之間一致嘅使用體驗。', '管理、支援同受保護嘅工作界面。'],
    investorsDirectionTitle: '平台方向',
    investorsDirectionList: [
        'Mini-app、KYC、支付、物流、CRM、媒體與公共部門。',
        '面向聯盟與國家級規模的通訊，而不只是 consumer messenger。',
        '面向區域部署同垂直場景嘅靈活架構。',
    ],
    contactBackHomeLabel: '首頁',
    contactCompanyLabel: '客戶',
    contactCopyEmailFailedLabel: '無法複製電郵',
    contactCopyEmailLabel: '複製電郵',
    contactCopyEmailSuccessLabel: '電郵已複製',
    contactDetailsLinkLabel: '聯絡方式同登記資料',
    contactEmailLabel: '電郵',
    contactFooterCopy: 'Chattenger 服務由個體經營者提供。',
    contactFooterTitle: '客戶資料',
    contactInnLabel: 'INN',
    contactLegalNameLabel: '客戶',
    contactOgrnipLabel: 'OGRNIP',
    contactPageDescription: '提供 Chattenger 服務嘅客戶資料。',
    contactPageTitle: '聯絡方式',
    contactPhoneLabel: '電話',
    contactWebsiteLabel: '網站',
};

const RU_REFINED: LandingContent = {
    ...RU,
    kicker: '',
    promise: 'Для людей, команд и компаний',
    title: 'Чаты и звонки и встречи',
    heroRotator: {
        subjects: ['Чаты', 'Звонки', 'Встречи'],
        connector: 'для',
        audiences: ['людей', 'команд', 'компаний'],
    },
    description: 'Близкие ближе.',
    aliases: 'Один аккаунт, одна история общения и один привычный интерфейс на телефоне и в браузере.',
    heroPills: ['Чаты и каналы', 'Звонки и встречи', 'Телефон и веб'],
    signalItems: [
        'Личные переписки, групповые чаты и каналы без перегруженного интерфейса.',
        'Созвоны, встречи, демонстрация экрана и записи для работы и повседневного общения.',
        'Один аккаунт и одна история сообщений на телефоне и в браузере.',
    ],
    featurePairs: [
        ['Личные чаты', 'Командные чаты'],
        ['Каналы', 'Группы'],
        ['Звонки', 'Встречи'],
        ['Демонстрация экрана', 'Записи'],
        ['Файлы', 'Фото и видео'],
        ['Поиск по истории', 'Поддержка'],
    ],
    extensionsTitle: 'Платформа, которая может расти дальше',
    extensionItems: ['Платежи', 'Документы', 'Поддержка', 'Логистика', 'Маркетплейсы', 'Обучение', 'Медиа', 'Новости', 'Сообщества', 'Госсектор'],
    architectureCopy:
        'Chattenger строится как продукт, которому можно доверить и личное общение, и рабочие процессы. В основе — понятный интерфейс, устойчивые звонки, контроль над инфраструктурой и спокойный, привычный UX.',
    architectureItems: [
        'Шифрование сообщений и аккуратная работа с ключами на стороне пользователя.',
        'Собственная инфраструктура звонков для встреч, записей и демонстрации экрана.',
        'Независимый серверный контур с расчётом на рост нагрузки и локальные внедрения.',
        'Предсказуемая синхронизация между телефоном и вебом.',
    ],
    outOfBoxTitle: 'Что уже есть',
    outOfBoxItems: [
        'Личные и командные чаты, каналы и поиск по истории.',
        'Звонки, встречи, демонстрация экрана и записи.',
        'Файлы, фото, видео и голосовые сообщения.',
        'Один аккаунт на телефоне и в браузере.',
        'Администрирование, поддержка и защищённые рабочие поверхности.',
    ],
    // Investor copy is intentionally blank on the public landing until a dedicated surface returns.
    investorsEyebrow: '',
    investorsTitle: '',
    investorsDescription: '',
    investorsNowTitle: '',
    investorsNowList: [],
    investorsDirectionTitle: '',
    investorsDirectionList: [],
    contactCompanyLabel: 'Клиент',
    contactEmailLabel: 'Email',
    contactPhoneLabel: 'Телефон',
};

const EN_REFINED: LandingContent = {
    ...EN,
    kicker: '',
    promise: 'For people, teams, and companies',
    title: 'Chats, calls, and meetings',
    heroRotator: {
        subjects: ['Chats', 'Calls', 'Meetings'],
        connector: 'for',
        audiences: ['people', 'teams', 'companies'],
    },
    description: 'One call closer.',
    aliases: 'Phone and browser feel like one product: one account, one history, and one familiar experience.',
    heroPills: ['Chats & channels', 'Calls & recordings', 'Phone + web'],
    signalItems: [
        'Direct messaging, groups, and channels without a cluttered interface.',
        'Voice, video, screen sharing, and recordings for real work scenarios.',
        'One account and a synchronized experience across phone and browser.',
    ],
    featurePairs: [
        ['Direct messaging', 'Team chats'],
        ['Channels', 'Groups'],
        ['Calls', 'Screen sharing'],
        ['Files', 'Photos and video'],
        ['Recordings', 'History search'],
        ['Support', 'Administration'],
    ],
    outOfBoxItems: [
        'Private and group chats, file sharing, and history search.',
        'Channels and large groups with roles and moderation.',
        'Voice messages, scheduled send, and delayed delivery.',
        'Group calls, video, screen sharing, and recordings.',
        'Administration, support, and protected work surfaces.',
    ],
    extensionsTitle: 'Designed to expand with the product',
    investorsEyebrow: '',
    investorsTitle: '',
    investorsDescription: '',
    investorsNowTitle: '',
    investorsNowList: [],
    investorsDirectionTitle: '',
    investorsDirectionList: [],
    contactCompanyLabel: 'Customer',
    contactEmailLabel: 'Email',
    contactPhoneLabel: 'Phone',
};

const ES: LandingContent = {
    ...EN_REFINED,
    promise: 'Para personas, equipos y empresas',
    title: 'Chats, llamadas y reuniones',
    heroRotator: {
        subjects: ['Chats', 'Llamadas', 'Reuniones'],
        connector: 'para',
        audiences: ['personas', 'equipos', 'empresas'],
    },
    description: 'Una llamada. Más cerca.',
    downloadUnavailableNotice: {
        description: 'La compilación aún se está subiendo o no está disponible temporalmente. Vuelve a intentarlo pronto.',
        title: 'La APK preliminar de Android no está disponible temporalmente',
    },
    appDownloadCtaLabel: 'Obtener la app',
    signInPrompt: '¿Ya tienes cuenta?',
    signInCtaLabel: 'Entrar',
    aliases: 'Chats, llamadas, canales, grabaciones y acceso web sin fricción innecesaria.',
    heroPills: ['Chats y canales', 'Llamadas y grabaciones', 'Web + móvil'],
    investorsTitle: 'Por qué este mercado resulta atractivo ahora',
    investorsDescription:
        'La demanda de productos locales de comunicación sigue creciendo. Las personas y las empresas quieren un servicio moderno, confiable, seguro y disponible tanto en el teléfono como en el navegador.',
    investorsNowTitle: 'Qué ya existe',
    investorsDirectionTitle: 'Hacia dónde puede crecer',
    contactCompanyLabel: 'Empresa',
    contactCopyEmailFailedLabel: 'No se pudo copiar el correo',
    contactCopyEmailLabel: 'Copiar correo',
    contactCopyEmailSuccessLabel: 'Correo copiado',
    contactDetailsLinkLabel: 'Contactos y datos',
    contactEmailLabel: 'Correo',
    contactFooterCopy: 'El servicio Chattenger es prestado por un empresario individual.',
    contactFooterTitle: 'Información del cliente',
    contactInnLabel: 'INN',
    contactLegalNameLabel: 'Cliente',
    contactOgrnipLabel: 'OGRNIP',
    contactPageDescription: 'Información del cliente que presta el servicio Chattenger.',
    contactPageTitle: 'Contactos',
    contactPhoneLabel: 'Teléfono',
    contactWebsiteLabel: 'Sitio web',
    contactBackHomeLabel: 'Inicio',
};

const FR: LandingContent = {
    ...EN_REFINED,
    promise: 'Pour les personnes, les équipes et les entreprises',
    title: 'Chats, appels et réunions',
    heroRotator: {
        subjects: ['Chats', 'Appels', 'Réunions'],
        connector: 'pour',
        audiences: ['les personnes', 'les équipes', 'les entreprises'],
    },
    description: 'Un appel. Plus proches.',
    downloadUnavailableNotice: {
        description: "La compilation est encore en cours d'envoi ou temporairement indisponible. Réessayez dans un instant.",
        title: "L'APK Android de prévisualisation est temporairement indisponible",
    },
    appDownloadCtaLabel: "Obtenir l'app",
    signInPrompt: 'Vous avez déjà un compte ?',
    signInCtaLabel: 'Connexion',
    aliases: 'Chats, appels, canaux, enregistrements et accès web sans friction inutile.',
    heroPills: ['Chats et canaux', 'Appels et enregistrements', 'Web + mobile'],
    investorsTitle: 'Pourquoi ce marché paraît porteur maintenant',
    investorsDescription:
        'La demande pour des produits locaux de communication continue de monter. Les personnes et les entreprises veulent un service moderne, fiable, sûr et disponible à la fois sur mobile et dans le navigateur.',
    investorsNowTitle: 'Ce qui existe déjà',
    investorsDirectionTitle: 'Ce que la plateforme peut devenir',
    contactCompanyLabel: 'Société',
    contactCopyEmailFailedLabel: "Impossible de copier l'e-mail",
    contactCopyEmailLabel: "Copier l'e-mail",
    contactCopyEmailSuccessLabel: 'E-mail copié',
    contactDetailsLinkLabel: 'Contacts et mentions',
    contactEmailLabel: 'E-mail',
    contactFooterCopy: 'Le service Chattenger est fourni par un entrepreneur individuel.',
    contactFooterTitle: 'Informations client',
    contactInnLabel: 'INN',
    contactLegalNameLabel: 'Client',
    contactOgrnipLabel: 'OGRNIP',
    contactPageDescription: 'Informations sur le client qui fournit le service Chattenger.',
    contactPageTitle: 'Contacts',
    contactPhoneLabel: 'Téléphone',
    contactWebsiteLabel: 'Site web',
    contactBackHomeLabel: 'Accueil',
};

const AR_REFINED: LandingContent = {
    ...AR,
    kicker: '',
    promise: 'للأفراد والفرق والشركات',
    title: 'الدردشات والمكالمات والاجتماعات',
    heroRotator: {
        subjects: ['دردشات', 'مكالمات', 'اجتماعات'],
        connector: 'لـ',
        audiences: ['الأفراد', 'الفرق', 'الشركات'],
    },
    description: 'مكالمة واحدة تقرّبكم.',
    featurePairs: [
        ['الدردشات الخاصة', 'دردشات الفرق'],
        ['القنوات', 'المجموعات'],
        ['المكالمات', 'الاجتماعات'],
        ['مشاركة الشاشة', 'التسجيلات'],
        ['الملفات', 'الصور والفيديو'],
        ['البحث في السجل', 'الدعم'],
    ],
    outOfBoxItems: [
        'محادثات خاصة وجماعية وملفات وبحث في السجل.',
        'قنوات ومجموعات كبيرة مع أدوار وإشراف.',
        'رسائل صوتية وإرسال مجدول ومؤجل.',
        'مكالمات جماعية وفيديو ومشاركة شاشة وتسجيلات.',
        'إدارة ودعم ومساحات عمل محمية.',
    ],
    investorsEyebrow: '',
    investorsTitle: '',
    investorsDescription: '',
    investorsNowTitle: '',
    investorsNowList: [],
    investorsDirectionTitle: '',
    investorsDirectionList: [],
    contactCompanyLabel: 'العميل',
    contactEmailLabel: 'البريد',
    contactPhoneLabel: 'الهاتف',
};

const ZH_CN_REFINED: LandingContent = {
    ...ZH_CN,
    kicker: '',
    promise: '面向个人、团队与企业',
    title: '聊天、通话和会议',
    heroRotator: {
        subjects: ['聊天', '通话', '会议'],
        connector: '面向',
        audiences: ['个人', '团队', '企业'],
    },
    description: '一通电话，更近一点。',
    featurePairs: [
        ['私聊', '团队聊天'],
        ['频道', '群组'],
        ['通话', '会议'],
        ['屏幕共享', '录制'],
        ['文件', '照片与视频'],
        ['历史搜索', '支持'],
    ],
    outOfBoxItems: [
        '私聊、群聊、文件与历史搜索。',
        '频道与大型群组，支持角色与审核。',
        '语音消息、定时发送和延迟发送。',
        '群组通话、视频、屏幕共享与录制。',
        '管理、支持与受保护的工作界面。',
    ],
    investorsEyebrow: '',
    investorsTitle: '',
    investorsDescription: '',
    investorsNowTitle: '',
    investorsNowList: [],
    investorsDirectionTitle: '',
    investorsDirectionList: [],
    contactCompanyLabel: '客户',
    contactEmailLabel: '邮箱',
    contactPhoneLabel: '电话',
};

const ZH_HK_REFINED: LandingContent = {
    ...ZH_HK,
    kicker: '',
    promise: '面向個人、團隊與企業',
    title: '聊天、通話同會議',
    heroRotator: {
        subjects: ['聊天', '通話', '會議'],
        connector: '面向',
        audiences: ['個人', '團隊', '企業'],
    },
    description: '一個電話，近一點。',
    featurePairs: [
        ['私聊', '團隊聊天'],
        ['頻道', '群組'],
        ['通話', '會議'],
        ['螢幕分享', '錄製'],
        ['檔案', '相片與影片'],
        ['歷史搜尋', '支援'],
    ],
    outOfBoxItems: [
        '私聊、群聊、檔案與歷史搜尋。',
        '頻道與大型群組，支援角色與審核。',
        '語音訊息、預約發送同延遲發送。',
        '群組通話、影片、螢幕分享與錄製。',
        '管理、支援同受保護嘅工作界面。',
    ],
    investorsEyebrow: '',
    investorsTitle: '',
    investorsDescription: '',
    investorsNowTitle: '',
    investorsNowList: [],
    investorsDirectionTitle: '',
    investorsDirectionList: [],
    contactCompanyLabel: '客戶',
    contactEmailLabel: '電郵',
    contactPhoneLabel: '電話',
};

export const landingContentByLocale: Record<SupportedLocale, LandingContent> = {
    ru: RU_REFINED,
    en: EN_REFINED,
    es: ES,
    fr: FR,
    ar: AR_REFINED,
    'zh-CN': ZH_CN_REFINED,
    'zh-HK': ZH_HK_REFINED,
};

export const DEFAULT_LANDING_CONTENT = RU_REFINED;

export const getLandingContent = (locale: SupportedLocale): LandingContent => landingContentByLocale[locale];
